// apps/admin/src/pages/auth/ResetPasswordPage.tsx
//
// vg-binding: CONTEXT:decisions (P999.11.D-09/D-15/D-16/D-08 — auth cluster to pw tokens)
// vg-binding: INTERFACE-STANDARDS:error-shape (N/A — pure FE restyle, no API surface)
//
// Phase 1 Task 47 — Reset password screen (admin portal).
//
// Per CONTEXT + SPECS / AC-08:
//   - Reads `?token=xxx` from URL.
//   - newPassword + confirmPassword form, validated client-side against
//     the policy (≥12 chars, 1 upper, 1 number, 1 special).
//   - Password requirements checklist updates live as the user types.
//   - On 200 → redirect to /login with a flash message in router state.
//   - On envelope error code `AUTH_PASSWORD_REUSED` → "Password used recently".
//   - On 401 (token used / expired) → render the "link already used or expired"
//     terminal state so the user cannot retry without re-requesting a link.

import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import {
  AuthSplitLayout,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Form,
  FormField,
  FormLabel,
  FormMessage,
  Input,
} from '@printway/ui';
import { apiClient } from '@printway/shared/api';

// Phase 8.x debug B1 — pre-auth locale switcher.
import { AdminLanguageSelector } from '../../components/shell/AdminLanguageSelector.js';
import { useTranslation } from '../../i18n/useTranslation.js';

// ---------------------------------------------------------------------------
// Password policy — mirrors @printway/zod-schemas PasswordSchema.
// Inlined here so the package does not need to be a dep of the SPA shells.
// ---------------------------------------------------------------------------

type Translate = (key: string, vars?: Record<string, unknown>) => string;

const SPECIAL_CHARS_RE = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/;

interface PolicyCheck {
  id: string;
  labelKey: string;
  test: (pw: string) => boolean;
}

const PASSWORD_RULES: readonly PolicyCheck[] = [
  { id: 'len', labelKey: 'auth.reset.rules.len', test: (pw) => pw.length >= 12 },
  {
    id: 'upper',
    labelKey: 'auth.reset.rules.upper',
    test: (pw) => /[A-Z]/.test(pw),
  },
  { id: 'num', labelKey: 'auth.reset.rules.num', test: (pw) => /[0-9]/.test(pw) },
  {
    id: 'special',
    labelKey: 'auth.reset.rules.special',
    test: (pw) => SPECIAL_CHARS_RE.test(pw),
  },
];

function makeResetPasswordSchema(t: Translate) {
  return z
    .object({
      newPassword: z
        .string()
        .min(12, t('auth.reset.validation.lenMin'))
        .refine((p) => /[A-Z]/.test(p), t('auth.reset.validation.upper'))
        .refine((p) => /[0-9]/.test(p), t('auth.reset.validation.num'))
        .refine(
          (p) => SPECIAL_CHARS_RE.test(p),
          t('auth.reset.validation.special'),
        ),
      confirmPassword: z.string().min(1, t('auth.reset.validation.confirmRequired')),
    })
    .refine((d) => d.newPassword === d.confirmPassword, {
      message: t('auth.reset.validation.mismatch'),
      path: ['confirmPassword'],
    });
}
interface ResetPasswordInput {
  newPassword: string;
  confirmPassword: string;
}

// ---------------------------------------------------------------------------
// Envelope helpers — narrow shape returned by /api/v1/auth/reset-password.
// ---------------------------------------------------------------------------

interface ErrorEnvelope {
  success: false;
  error: { code: string; message: string };
}

function extractErrorCode(payload: unknown): string | null {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as ErrorEnvelope).success === false &&
    'error' in payload
  ) {
    return (payload as ErrorEnvelope).error.code ?? null;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type ServerErrorState =
  | null
  | { kind: 'reused' }
  | { kind: 'tokenInvalid' }
  | { kind: 'generic'; message: string };

export function ResetPasswordPage(): JSX.Element {
  const { t } = useTranslation('ops');
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';
  const [serverError, setServerError] = useState<ServerErrorState>(null);
  const [tokenMissing, setTokenMissing] = useState<boolean>(false);
  // #1127 — validate the token ON LOAD so an expired/used link shows the
  // "link expired" screen immediately (not only after the user submits).
  const [validating, setValidating] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setTokenMissing(true);
      setValidating(false);
      return;
    }
    (async () => {
      try {
        const client = apiClient() as unknown as {
          GET: (p: string) => Promise<{ response: Response }>;
        };
        const { response } = await client.GET(
          `/api/v1/auth/reset-password/validate?token=${encodeURIComponent(token)}`,
        );
        if (!cancelled && !response.ok) setServerError({ kind: 'tokenInvalid' });
      } catch {
        // Network error → let the user try submitting; don't hard-block.
      } finally {
        if (!cancelled) setValidating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const ResetPasswordSchema = useMemo(() => makeResetPasswordSchema(t), [t]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordInput>({
    resolver: zodResolver(ResetPasswordSchema as never),
    mode: 'onChange',
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  const newPassword = watch('newPassword') ?? '';
  const checks = useMemo(
    () =>
      PASSWORD_RULES.map((rule) => ({
        id: rule.id,
        label: t(rule.labelKey),
        passed: rule.test(newPassword),
      })),
    [newPassword, t],
  );

  const onSubmit = async (data: ResetPasswordInput): Promise<void> => {
    setServerError(null);
    if (!token) {
      setTokenMissing(true);
      return;
    }
    try {
      const client = apiClient();
      const { response, error } = await (
        client as unknown as {
          POST: (
            path: string,
            init: { body: unknown },
          ) => Promise<{
            data?: unknown;
            error?: unknown;
            response: Response;
          }>;
        }
      ).POST('/api/v1/auth/reset-password', {
        body: { token, newPassword: data.newPassword },
      });

      if (response.ok) {
        navigate('/login', {
          replace: true,
          state: {
            flash: {
              variant: 'success' as const,
              title: t('auth.reset.flash.title'),
              description: t('auth.reset.flash.desc'),
            },
          },
        });
        return;
      }

      // Token used / expired → terminal "link invalid" screen.
      // BE returns 410 (reset-password handler); 401 kept for back-compat.
      if (response.status === 410 || response.status === 401) {
        setServerError({ kind: 'tokenInvalid' });
        return;
      }

      const code = extractErrorCode(error);
      if (code === 'AUTH_PASSWORD_REUSED') {
        setServerError({ kind: 'reused' });
        return;
      }

      setServerError({
        kind: 'generic',
        message:
          code ??
          t('auth.reset.errors.requestFailed', {
            status: String(response.status),
          }),
      });
    } catch (err) {
      setServerError({
        kind: 'generic',
        message:
          err instanceof Error ? err.message : t('auth.reset.errors.unknown'),
      });
    }
  };

  // While the on-load token check is in flight, show a spinner instead of
  // flashing the password form for a link that may already be expired.
  if (token && validating) {
    return (
      <AuthSplitLayout variant="marketing">
        <div className="flex flex-1 items-center justify-center">
          <div
            role="status"
            aria-label={t('auth.reset.validating')}
            className="h-6 w-6 animate-spin rounded-full border-2 border-pw-border border-t-pw-primary"
          />
        </div>
      </AuthSplitLayout>
    );
  }

  // ---------------------------------------------------------------------------
  // Terminal state — link missing or used/expired (no retry path here).
  // ---------------------------------------------------------------------------
  if (tokenMissing || serverError?.kind === 'tokenInvalid') {
    return (
      <AuthSplitLayout variant="marketing">
      <div className="flex justify-end">
        <AdminLanguageSelector />
      </div>
      <div className="flex flex-1 items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t('auth.reset.invalid.title')}</CardTitle>
            <CardDescription>
              {t('auth.reset.invalid.desc')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/forgot-password">
              <Button className="w-full">{t('auth.reset.invalid.requestNew')}</Button>
            </Link>
            <Link to="/login">
              <Button variant="outline" className="w-full">
                {t('auth.reset.backToLogin')}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </AuthSplitLayout>
    );
  }

  return (
    <AuthSplitLayout variant="marketing">
      <div className="flex justify-end">
        <AdminLanguageSelector />
      </div>
      <div className="flex flex-1 items-center justify-center">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('auth.reset.title')}</CardTitle>
          <CardDescription>
            {t('auth.reset.subtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <FormField>
              <FormLabel htmlFor="newPassword">{t('auth.reset.newPasswordLabel')}</FormLabel>
              <Input
                id="newPassword"
                type="password"
                autoComplete="new-password"
                {...register('newPassword')}
              />
              {errors.newPassword ? (
                <FormMessage>{errors.newPassword.message}</FormMessage>
              ) : null}
            </FormField>

            <ul className="space-y-1 text-xs" aria-label={t('auth.reset.rulesAria')}>
              {checks.map((c) => (
                <li
                  key={c.id}
                  className={
                    c.passed
                      ? 'text-emerald-600 dark:text-emerald-300'
                      : 'text-pw-text-muted'
                  }
                >
                  <span aria-hidden="true">{c.passed ? '✓' : '○'}</span>{' '}
                  {c.label}
                </li>
              ))}
            </ul>

            <FormField>
              <FormLabel htmlFor="confirmPassword">{t('auth.reset.confirmPasswordLabel')}</FormLabel>
              <Input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                {...register('confirmPassword')}
              />
              {errors.confirmPassword ? (
                <FormMessage>{errors.confirmPassword.message}</FormMessage>
              ) : null}
            </FormField>

            {serverError?.kind === 'reused' ? (
              <FormMessage>
                {t('auth.reset.errors.reused')}
              </FormMessage>
            ) : null}
            {serverError?.kind === 'generic' ? (
              <FormMessage>{serverError.message}</FormMessage>
            ) : null}

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? t('auth.reset.submitting') : t('auth.reset.submit')}
            </Button>
          </Form>
        </CardContent>
      </Card>
    </div>
    </AuthSplitLayout>
  );
}

export default ResetPasswordPage;
